/* global zaraz */
import { apiPostData } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    getRevisionVersion,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/utils';
import { dispatchTrackingEvent, QUICK_SEARCH_START_DATE_ONLY_SELECTED } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';
import userData from '@PROJECT_ROOT/assets/responsive-frontend/js/services/userData';

const getClarityErrorId = () => {
    if (typeof window === 'undefined'
        || typeof window.clarity !== 'function'
        || typeof zaraz === 'undefined'
        || !zaraz.consent
        || zaraz.consent.get('KlIi') !== true
    ) {
        return null;
    }

    const errorId = typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

    window.clarity('set', 'frontendErrorId', errorId);
    window.clarity('event', 'frontend_error');

    return errorId;
};

export const handleAxiosError = (error) => {
    const axiosCustomErrors = JSON.parse(document.getElementById('mainWrapper')?.getAttribute('data-axios'));
    let debugLogForThisError = false;
    if (axios.isAxiosError(error)) {
        switch (error.code) {
            case 'ECONNABORTED':
                debugLogForThisError = true;
                break;
            case 'ERR_NETWORK':
                toast.error(axiosCustomErrors?.connectionError);
                debugLogForThisError = true;
                break;
            case 'ERR_BAD_REQUEST':
                if (error.config?.method === 'post' || error.config?.method === 'options') {
                    toast.info(axiosCustomErrors?.unavailableText);
                }
                break;
            default:
                break;
        }
    }
    return debugLogForThisError;
};

const isBot = (userAgent) => {
    const botPatterns = [
        /Googlebot.*www\.google\.com\/bot\.html/,
        /bingbot.*www\.bing\.com\/bingbot\.htm/,
        /YandexMobileBot\/3\.0.*yandex\.com\/bots/,
        /AdsBot-Google-Mobile.*www\.google\.com\/mobile\/adsbot\.html/,
        /Google-Read-Aloud.*support\.google\.com\/webmasters\/answer\/1061943/,
        /AdsBot-Google.*www\.google\.com\/adsbot\.html/,
        /YandexBot\/3\.0.*yandex\.com\/bots/,
        /Baiduspider-render\/2\.0.*www\.baidu\.com\/search\/spider\.html/,
        /Applebot\/0\.1;.*http:\/\/www\.apple\.com\/go\/applebot/,
        /meta-externalagent\/1\.1.*developers\.facebook\.com\/docs\/sharing\/webmasters\/crawler/,
        /PingdomPageSpeed\/1\.0.*pingbot\/2\.0.*pingdom\.com/,
        /meta-webindexer\/1\.1.*developers\.facebook\.com\/docs\/sharing\/webmasters\/crawler/,
    ];
    return botPatterns.some((pattern) => pattern.test(userAgent));
};

const getRuntimeDebugInfo = () => ({
    webdriver: navigator.webdriver === true,
    jqueryExists: typeof window.jQuery !== 'undefined',
    dollarExists: typeof window.$ !== 'undefined',
    jqueryVersion: window.jQuery?.fn?.jquery || null,
    bootstrapExists: typeof window.bootstrap !== 'undefined',
    bootstrapJqueryPluginExists: typeof window.jQuery?.fn?.modal !== 'undefined',
    online: navigator.onLine,
    documentReadyState: document.readyState,
});

const reportLog = (errorData, debugOnly) => {
    // TODO: Potrebno odrediti koje sve informacije želimo slati kad dođe do error-a.
    if (axios.isCancel(errorData)) return;
    const debugLevelAxiosError = handleAxiosError(errorData);
    const postPath = debugLevelAxiosError || debugOnly ? 'log/debug' : 'log/error';
    const browserInfo = {
        userAgent: window.navigator.userAgent,
        language: window.navigator.language,
        platform: window.navigator.platform,
        // TODO: Pronaći altenativu jer na navedenom browseru nećemo imati ovaj podatak.
        // Disable-ano jer ga ne ne podržava safari 16.1.
        // eslint-disable-next-line compat/compat
        cookiesEnabled: window.navigator.cookieEnabled,
        online: window.navigator.onLine,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        colorDepth: window.screen.colorDepth,
    };
    if (isBot(browserInfo.userAgent)) {
        return;
    }
    const runtimeDebugInfo = getRuntimeDebugInfo();
    // Koristi isključivo za prepoznavanje i gušenje nefunkcionalnih logova.
    // eslint-disable-next-line no-underscore-dangle
    const isE2ETest = windowManipulationService.isBrowserEnvironment() ? window.__E2E_TEST__ : false;
    const user = userData.getDecodedUserDataFromLocalStorage();
    const errorInfo = {
        message: errorData.message || 'Nepoznato',
        stack: errorData.stack || 'Nepoznato',
        name: errorData.name || 'Nepoznato',
        fileName: errorData.fileName || 'Nepoznato',
        lineNumber: errorData.lineNumber || 'Nepoznato',
        columnNumber: errorData.columnNumber || 'Nepoznato',
        cause: errorData.cause || 'Nepoznato',
        code: errorData.code || 'Nepoznato',
        e2eTest: isE2ETest ? 'Playwright E2E test' : 'Nepoznato',
        details: errorData.details || 'Nepoznato',
        timestamp: new Date().toLocaleString('en-GB', { timeZone: 'Europe/Zagreb' }),
        loggedUser: user?.username ? 'yes' : 'no',
        logSource: 'reportService - JS',
    };
    if (isE2ETest && errorInfo.stack.includes('/cdn-cgi/zaraz/')) return;
    const clarityErrorId = getClarityErrorId();
    const preparedData = {
        errorName: errorData.code || errorData.name || 'ERROR NAME NIJE ZABILJEŽEN',
        errorOnPage: encodeURI(window.location.href) || 'Nepoznato',
        referrerPageToPageOnWhichErrorOcured: encodeURI(document.referrer) || 'Nepoznato',
        revision: getRevisionVersion(),
        errorInfo,
        errorData,
        browserInfo,
        runtimeDebugInfo,
        ...(clarityErrorId && { clarityErrorId }),
    };
    const completedData = {
        logText: JSON.stringify(preparedData, null, 4),
    };
    apiPostData(postPath, completedData).catch((currentError) => {
        // Kad nismo u mogućnosti dojaviti error, ispisujemo ga. (Možda da imamo neki vanjski servis za dojavu ovakvih slučajeva).
        // U CSR-u ne ispisujemo podatke o erroru koji se nije mogao dojaviti jer nema smisla.
        // eslint-disable-next-line
        console.error(
            `Dojava error-a(CSR) neuspješna, podaci o error-u: ${JSON.stringify({
                message: currentError?.message,
                code: currentError?.code,
                unreportedError: completedData,
            }, null, 4)}`,
        );
    });
};

export const reportError = (errorData) => {
    reportLog(errorData, false);
};

export const reportDebug = (debugData) => {
    reportLog(debugData, true);
};

export const reportErrorSSR = (errorData) => {
    const postPath = 'log/error';
    // Koristi isključivo za prepoznavanje i gušenje nefunkcionalnih logova.
    // eslint-disable-next-line no-underscore-dangle
    const isE2ETest = windowManipulationService.isBrowserEnvironment() ? window.__E2E_TEST__ : false;
    const errorInfo = {
        message: errorData?.message || 'Nepoznato',
        stack: errorData?.stack || 'Nepoznato',
        name: errorData?.name || 'Nepoznato',
        fileName: errorData?.fileName || 'Nepoznato',
        lineNumber: errorData?.lineNumber || 'Nepoznato',
        columnNumber: errorData?.columnNumber || 'Nepoznato',
        cause: errorData?.cause || 'Nepoznato',
        code: errorData?.code || 'Nepoznato',
        e2eTest: isE2ETest ? 'Playwright E2E test' : 'Nepoznato',
        details: errorData?.details || 'Nepoznato',
        timestamp: new Date().toLocaleString('en-GB', { timeZone: 'Europe/Zagreb' }),
    };
    if (isE2ETest && errorInfo.stack.includes('/cdn-cgi/zaraz/')) return;
    const preparedData = {
        errorName: errorData?.code || errorData?.name || 'ERROR NAME NIJE ZABILJEŽEN',
        errorOnPage: 'SSR ERROR',
        customMessage: errorData?.customMessage || 'Nepoznato',
        errorInfo,
        errorData: JSON.stringify(errorData),
    };
    const completedData = {
        logText: JSON.stringify(preparedData, null, 4),
    };

    apiPostData(postPath, completedData).catch((currentError) => {
        // Kad nismo u mogućnosti dojaviti error, ispisujemo ga. (Možda da imamo neki vanjski servis za dojavu ovakvih slučajeva).
        // eslint-disable-next-line
        console.error('Dojava error-a(SSR) neuspješna, podaci o error-u:', {
            // Razlog nemogućnosti dojave.
            message: currentError?.message,
            code: currentError?.code,
            // Error koji nije mogao bit dojavljen.
            unreportedError: completedData,
        });
    });
};

// Dodano privremeno zbog #59779, obrisati kasnije.
export const reportOnlyStartDateSelected = () => {
    if (isBot(window.navigator.userAgent)) return;
    try {
        dispatchTrackingEvent(QUICK_SEARCH_START_DATE_ONLY_SELECTED);
    } catch (err) {
        reportError(err);
    }
};

const getReferrerHost = (ref) => {
    if (!ref || typeof ref !== 'string') {
        return '';
    }
    let r = ref.toLowerCase();
    if (r.startsWith('http://')) r = r.slice(7);
    else if (r.startsWith('https://')) r = r.slice(8);
    const slash = r.indexOf('/');
    return slash === -1 ? r : r.slice(0, slash);
};

export const reportPageLeaveFrameHTMLElement = () => {
    if (isBot(window.navigator.userAgent)) return;
    const ref = document.referrer;
    if (!ref || ref.startsWith(ADRIATIC_PARTNER_RESPONSIVE_SCHEME_HOST)
        || ref.startsWith(ADRIATIC_RESPONSIVE_SCHEME_HOST)) {
        return;
    }
    const refFirst20Char = ref.substring(0, 20);
    if (refFirst20Char.includes('com.') || refFirst20Char.includes('Gmail\\')) {
        return;
    }
    const ignoredReferrerHosts = [
        'facebook.com',
        'mail.google.com',
        // Partnerski admin hostovi koji namjerno otvaraju FRAME izvan okvira (njihove interne/admin aplikacije)
        'poczta.int.pl',
    ];
    const referrerHost = getReferrerHost(ref);
    if (ignoredReferrerHosts.some((ignoredReferrerHost) => referrerHost === ignoredReferrerHost
        || referrerHost.endsWith(`.${ignoredReferrerHost}`))) {
        return;
    }
    try {
        const url = new URL(window.location.href);
        const fsSet = url.searchParams.get('fs_set');
        const endpointURL = `${window.origin}/rsb-service/log/warning${fsSet ? `?fs_set=${fsSet}` : ''}`;
        const data = {
            message: 'FRAME servis izašao iz partnerskog okvira. Provjeriti o čemu se radi.',
            errorOnPage: encodeURI(window.location.href) || 'Nepoznato',
            referrerPageToPageOnWhichErrorOcured: encodeURI(ref) || 'Nepoznato',
            timestamp: new Date().toLocaleString('en-GB', { timeZone: 'Europe/Zagreb' }),
            browserInfo: {
                userAgent: window.navigator.userAgent,
                language: window.navigator.language,
                platform: window.navigator.platform,
                // TODO: Pronaći altenativu jer na navedenom browseru nećemo imati ovaj podatak.
                // Disable-ano jer ga ne ne podržava safari 16.1.
                // eslint-disable-next-line compat/compat
                cookiesEnabled: window.navigator.cookieEnabled,
                online: window.navigator.onLine,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                colorDepth: window.screen.colorDepth,
            },
        };
        const blob = new Blob([JSON.stringify({ logText: JSON.stringify(data) })], {
            type: 'application/json',
        });
        navigator.sendBeacon(endpointURL, blob);
    } catch (err) {
        reportError(err);
    }
};
